import { defineStore } from 'pinia';
import * as api from "../../utils/api.js";
import { computeHash, formatDate } from "../../utils/helpers";

export const useNotificationsStore = defineStore({
  id: 'notifications',
  state: () => ({
    loadingNotifications: false,
    count: {
      unread: 0,
      total: 0,
    },
    notifications: [],
    allNotifications: [], // Store all notifications for client-side pagination
    notificationTypes: [],
    error: null,
  }),
  getters: {
    getNotificationCount: state => state.count?.total,
    getUnreadCount: state => state?.count?.unread,
    getNotificationTypes: state => type => state?.notificationTypes?.filter(n => n.type === type) ?? [],
  },
  actions: {
    async loadNotificationCount() {
      try {
        let response = await api.get("admin/notification/count");
        this.count = response?.data;
      } catch (error) {
        console.error("Failed to load notification count:", error);
        this.error = error;
      }
    },
    async loadNotifications(state, options = {}) {
      try {
        this.loadingNotifications = true;
        
        // Since the backend doesn't support pagination yet, we'll load all notifications
        // and handle pagination on the client side
        let originalHash = computeHash(state?.notifications?.map(notification => `${notification.id}-${notification.read}-${notification.patientName}`));
        
        // Build query parameters for sorting and filtering
        const queryParams = new URLSearchParams();
        queryParams.append('_sort', '-created');
        
        // Add any additional filters if needed
        // if (options?.typeFilter && options.typeFilter !== 'All') {
        //   queryParams.append('type', options.typeFilter);
        // }
        
        if (options?.readFilter && options.readFilter !== 'All') {
          queryParams.append('read', options.readFilter === true);
        }
        
        const url = `admin/notification?${queryParams.toString()}`;
        var response = await api.get(url);
        
        let newHash = computeHash(response?.data?.map(notification => `${notification.id}-${notification.read}-${notification.patientName}`));
        
        // Only update if the data has changed
        if (originalHash !== newHash) {
          // Store all notifications
          this.allNotifications = response?.data;
          
          // Update total count
          this.count = {
            ...this.count,
            total: this.allNotifications.length,
            unread: this.allNotifications.filter(n => !n.read).length
          };
          
          // Apply client-side pagination and filtering
          this.applyFiltersAndPagination(options);
        } else if (options) {
          // If data hasn't changed but options have, just apply the new pagination/filtering
          this.applyFiltersAndPagination(options);
        }
      } catch (error) {
        console.error("Failed to load notifications:", error);
        this.error = error;
      } finally {
        this.loadingNotifications = false;
      }
    },
    async loadNotificationTypes() {
      try {
        let response = await api.get("admin/notificationType");
        this.notificationTypes = response?.data;
      } catch (error) {
        console.error("Failed to load notification types:", error);
        this.error = error;
      }
    },
    async deleteNotifications(ids, options = {}) {
      await api.post("admin/notification/delete", ids);
      
      // Update both notifications and allNotifications arrays
      this.notifications = this.notifications.filter(n => !ids.includes(n.id));
      
      if (this.allNotifications) {
        this.allNotifications = this.allNotifications.filter(n => !ids.includes(n.id));
        
        // Update count
        this.count = {
          ...this.count,
          total: this.allNotifications.length,
          unread: this.allNotifications.filter(n => !n.read).length
        };
        
        // Apply pagination and filtering with current options
        this.applyFiltersAndPagination(options);
      }
    },
    updateNotification(n) {
      return api.put("admin/notification/update", n);
    },
    resetNotifications() {
      this.notifications = [];
    },
    loadNotificationTypesSuccess(notificationTypes) {
      this.notificationTypes = notificationTypes;
    },
    async markAllAsRead() {
      try {
        await api.put("admin/notification/markAllRead");
        this.notifications.forEach(n => n.read = true);
        if (this.allNotifications) {
          this.allNotifications.forEach(n => n.read = true);
        }
      } catch (error) {
        console.error("Failed to mark all notifications as read:", error);
        this.error = error;
      }
    },
    
    // Apply client-side pagination and filtering
    applyFiltersAndPagination(options = {}) {
      if (!this.allNotifications) return;
      
      // Default pagination parameters
      const page = options.page || 1;
      const itemsPerPage = options.itemsPerPage || 50;
      const typeFilter = options.typeFilter || 'All';
      const readFilter = options.readFilter || 'All';
      
      // Apply filters
      let filtered = [...this.allNotifications];
      
      // Apply type filter if not 'All'
      if (typeFilter !== 'All') {
        filtered = filtered.filter(n => {        
          return n.type === typeFilter || n.type.includes(typeFilter);
        });
      }
      
      // Apply read filter if not 'All'
      if (readFilter !== 'All') {
        filtered = filtered.filter(n => n.read === readFilter);
      }
      
      // Update count with filtered total
      this.count = {
        ...this.count,
        total: filtered.length
      };
      
      // Apply pagination
      const startIndex = (page - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      
      // Set the paginated notifications
      this.notifications = filtered.slice(startIndex, endIndex);
    }
  },
});
